import { computed, ref, useContext } from '@nuxtjs/composition-api';
import { CustomerAddress } from '@shared/composables/useAddress';
import { EmailMarketingState } from '@shared/composables/useEmailMarketing';
import { Logger } from '@vue-storefront/core';

import getCustomerDetails from './getCustomerDetails';
import mapToCustomer from './mapToCustomer';

export interface Customer {
  id?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  emailAddress?: string;
  emailMarketingState?: EmailMarketingState;
  phoneNumber?: string;
  companyLocationId?: string;
  isPayNowOnly?: boolean;
  addresses: CustomerAddress[];
  defaultAddress?: CustomerAddress;
}

const customer = ref<Customer | null>(null);

const useCustomer = () => {
  const { $config } = useContext();

  const error = ref<string | null>(null);

  const isLoading = ref(false);

  const loadCustomer = async ({ accessToken }: { accessToken: string }) => {
    try {
      const customerDetails = await getCustomerDetails({
        accessToken,
        customerAccountEndpoint: $config.customerAccountEndpoint,
      });

      customer.value = mapToCustomer(customerDetails);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to fetch customer.';
      Logger.error(errorMessage);
      error.value = errorMessage;
    } finally {
      isLoading.value = false;
    }
  };

  const isInternational = computed(
    () => customer.value && customer.value.defaultAddress?.country?.toLowerCase() !== 'australia',
  );

  const isB2BCustomer = computed(() => customer.value?.companyLocationId);

  const isInvoiceEnabled = computed(() => customer.value?.isPayNowOnly === false);

  return {
    customer,
    loadCustomer,
    error,
    isLoading,
    isInternational,
    isB2BCustomer,
    isInvoiceEnabled,
  };
};

export default useCustomer;
